import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { CHAT_MESSAGES_PAGE_LIMIT } from '#modules/chat/components/Chats/hooks/useChatMessages';
import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { InviteToSessionDialogById } from '#modules/finder/features/posts/components/Post/InviteFriendDialog/ById';
import { SessionDetailsDialogById } from '#modules/finder/features/posts/components/Post/SessionDetailsDialog/ById';
import { getSessionNotificationSummaryQueryOptions } from '#modules/finder/features/posts/model/sessionNotification';
import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { useProfile } from '#modules/user/features/profile/hooks/useProfile';
import { trackRoomExpanded } from '#features/analytics/utils/sessionRoomFunnel';
import { useCallStore } from '#features/calls/model/callStore';
import { chatMessagesQueryOptions } from '#features/chats/model/chats';
import { serverNow } from '#features/date/utils/serverClock';
import { MOBILE_BREAKPOINT_PX } from '#features/media-window/model';
import { leaveVoice } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import { useIsChatThread } from '#hooks/useIsChatThread';
import { useSSR } from '#hooks/useSSR';
import { useCurrentSession } from '../hooks/useCurrentSession';
import { ENDED_FAREWELL_MS, isRoomAllowedHere } from '../model/roomPresence';
import {
  PERSON_SYSTEM_KINDS,
  resolveLastMessageAuthor,
  resolveLastSystemActor,
  type CachedChatMessages,
  type LastSystemActor,
  type SessionRoomEntry,
} from '../model/roomSessions';
import { useSessionRoomStore } from '../model/sessionRoomStore';
import { isClosedPhase, resolveRoomPhase, type SessionRoomPhase } from './RoomChrome';
import { SessionRoomBar } from './SessionRoomBar';
import { SessionRoomPill } from './SessionRoomPill';
import { SessionRoomSheet } from './SessionRoomSheet';
import { SessionRoomWindow } from './SessionRoomWindow';

/**
 * Контракт с поверхностями комнаты.
 *
 * Он один на все четыре, и это не бюрократия: пилюля, окно, полоса и шторка – четыре размера
 * одного предмета. Начни каждая добывать сессию и считать фазу сама – «одна сессия за раз»
 * превратится в четыре разных ответа на одном экране.
 *
 * Всё, что поверхность может решить сама, в контракт не входит: режим, угол, край и раскрытие
 * состава каждая читает из `sessionRoomStore` напрямую. Через хост идёт только то, чего снизу не
 * видно, – выбранная сессия, её фаза и переходы, за которые отвечает маршрут.
 */
export type SessionRoomSurfaceProps = {
  /** Сессия, которую комната показывает. Ровно одна – выбор сделал `pickCurrentSession`. */
  session: SessionRoomEntry;
  phase: SessionRoomPhase;
  /**
   * Уйти на весь экран – на маршрут треда. `null`, пока чат сессии не создан: открывать нечего,
   * и кнопка стоит выключенной с подписью «Чат создаётся…».
   */
  onFullscreen: (() => void) | null;
  /** Закрыть плашку прощания. Есть только в `ended`: до конца сессии «×» нет. */
  onClose: () => void;
  /**
   * Кто написал последнюю реплику – вторая строка пилюли и полосы: «Ник: го на мид».
   *
   * Идёт через хост по той же причине, что сессия и фаза: имени нет ни в одном поле строки
   * сессии, его достают из кеша переписки, и доставать его каждой поверхности отдельно значило бы
   * четыре подписки на один кеш ради одной строки. `null` – имени сейчас нет: переписку в этой
   * вкладке ещё не открывали, реплика служебная или её автор в составе не значится.
   */
  lastMessageAuthor: string | null;
  /**
   * О ком последняя служебная строка: «Вы вступили в сессию» вместо «Участник вступил» тому, кто
   * вступил сам. `null` – строка ни о ком или её не нашли.
   */
  lastSystemActor?: LastSystemActor | null;
};

/** Пилюля разворачивается в окно, полоса – в шторку: один жест, два размера. */
export type SessionRoomPillProps = SessionRoomSurfaceProps & { onExpand: () => void };
export type SessionRoomWindowProps = SessionRoomSurfaceProps & {
  onCollapse: () => void;
  /** Окно открыли жестом человека – забрать фокус, чтобы Escape работал сразу. */
  autoFocus?: boolean;
};
export type SessionRoomBarProps = SessionRoomSurfaceProps & { onExpand: () => void };
export type SessionRoomSheetProps = SessionRoomSurfaceProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Телефон это или десктоп – по-живому, а не одним замером на старте.
 *
 * Окно разговора читало ширину один раз при рендере и этим обходилось: оно живёт ровно пока идёт
 * разговор. Комната висит на экране часами, и человек за это время успевает и повернуть планшет,
 * и растянуть окно браузера – а от ответа зависит не отступ, а какая поверхность нарисована.
 */
const useIsMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX
  );

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const sync = () => setIsMobile(query.matches);

    sync();
    query.addEventListener('change', sync);

    return () => query.removeEventListener('change', sync);
  }, []);

  return isMobile;
};

/** На сервере кеша переписки нет вовсе, и комната там не рисуется. */
const noCachedMessages = () => undefined;

/**
 * Имя автора последней реплики – из того, что уже загружено.
 *
 * Строка сессии отдаёт текст последней реплики и не отдаёт, кто её написал: такого поля нет ни в
 * `sessionChat`, ни где-либо ещё на шлюзе. Зато есть её идентификатор, а сами сообщения этой
 * переписки лежат в кеше – их держит лента комнаты. Отсюда и правило: имя показываем, когда оно
 * УЖЕ есть, и не платим за него ни одним лишним запросом.
 *
 * Подписка – на кеш запросов, а не второй `useQuery`: свой наблюдатель означал бы второй запрос
 * той же переписки на каждой странице приложения, где висит одна свёрнутая пилюля. Здесь же
 * комната только подглядывает в чужой кеш и перерисовывается, лишь когда он правда изменился.
 *
 * Чего это стоит честно назвать: пока переписку в этой вкладке не открывали ни окном, ни
 * шторкой, ни тредом, кеша нет – и превью остаётся без имени, как было. Открыли раз – имя есть и
 * дальше, а новые реплики приезжают в тот же кеш сокетом.
 */
const useLastMessageAuthor = (
  entry: SessionRoomEntry | null
): { author: string | null; systemActor: LastSystemActor | null } => {
  const queryClient = useQueryClient();
  const chatId = entry?.chatId ?? null;
  const lastMessageId = entry?.lastMessageId ?? null;
  const members = entry?.members;

  const subscribe = useCallback(
    (onChange: () => void) => queryClient.getQueryCache().subscribe(onChange),
    [queryClient]
  );

  /*
    Ключ – тот же, что у ленты (`['chatMessages', chatId, 50]`), и это условие задачи: комната
    читает ровно то, что лента уже загрузила. Разойдись ключи – кеш был бы пуст всегда.
  */
  const readCache = useCallback(
    () =>
      chatId
        ? queryClient.getQueryData<CachedChatMessages>([
            'chatMessages',
            chatId,
            CHAT_MESSAGES_PAGE_LIMIT,
          ])
        : undefined,
    [chatId, queryClient]
  );

  const cache = useSyncExternalStore(subscribe, readCache, noCachedMessages);
  const { profile } = useProfile();
  const currentUserId = profile?.id ?? null;
  const lastMessageText = entry?.lastMessageText ?? null;

  const author = useMemo(
    () => resolveLastMessageAuthor({ lastMessageId, members, cache }),
    [cache, lastMessageId, members]
  );
  const cachedActor = useMemo(
    () => resolveLastSystemActor({ lastMessageId, lastMessageText, cache, currentUserId }),
    [cache, currentUserId, lastMessageId, lastMessageText]
  );

  /*
    Служебная строка о человеке, а ленты в кеше нет – это и есть случай вступившего: он только
    что нажал «Вступить», переписку ещё не открывал, и превью говорило ему о нём самом «Участник
    вступил». Здесь – единственное исключение из правила «ни одного запроса ради превью»: одна
    последняя строка, только на служебную строку о человеке и только пока кеш молчит. Ключ – по
    номеру строки: новое событие спросит заново, повторный рендер – нет.
  */
  const needsActor =
    Boolean(
      chatId && lastMessageId && lastMessageText && PERSON_SYSTEM_KINDS.has(lastMessageText)
    ) && !cachedActor;
  const { data: fetchedActor } = useQuery({
    queryKey: ['session-room-last-actor', chatId, lastMessageId],
    queryFn: async () => {
      const page = await chatMessagesQueryOptions({
        chatId: chatId!,
        offset: 0,
        limit: 1,
      }).queryFn();
      return resolveLastSystemActor({
        lastMessageId,
        lastMessageText,
        cache: { pages: [page] },
        currentUserId,
      });
    },
    enabled: needsActor && Boolean(currentUserId),
    staleTime: Infinity,
  });

  return { author, systemActor: cachedActor ?? (needsActor ? (fetchedActor ?? null) : null) };
};

/**
 * Единственная точка монтирования комнаты сессии.
 *
 * Хост отвечает на три вопроса и ни на один больше: какую сессию показывать
 * (`useCurrentSession`), можно ли её показывать здесь (правило экрана) и в каком виде (платформа
 * плюс режим из `sessionRoomStore`). Всё остальное – дело поверхностей: они сами читают стор,
 * сами считают свою геометрию и сами публикуют резерв снизу.
 *
 * Стоит в корне лейаута рядом с `CallOverlay`, то есть выше роутера: переход между страницами
 * комнату не перемонтирует, а значит не рвёт ни голос, ни набранный черновик.
 *
 * Плавающего окна разговора рядом больше нет – голос стал ярусом комнаты. Но соединением владеет
 * не хост, а `VoiceRoomProvider` на строчку выше, и это не мелочь: хост ничего не рисует там,
 * где комната запрещена – например на треде собственной сессии, где ярус уже стоит над лентой, –
 * и элементы `<audio>`, живи они здесь, замолкали бы ровно при переходе «на весь экран».
 */
export const SessionRoomHost = () => {
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const { isBrowser } = useSSR();
  const isAuth = useIsAuth();
  const isMobile = useIsMobileViewport();
  const navigate = useNavigate();

  const { session, sessions } = useCurrentSession();

  const mode = useSessionRoomStore((state) => state.mode);
  const setMode = useSessionRoomStore((state) => state.setMode);
  const setVisibleSessionId = useSessionRoomStore((state) => state.setVisibleSessionId);
  const enterFull = useSessionRoomStore((state) => state.enterFull);
  const exitFull = useSessionRoomStore((state) => state.exitFull);
  const dismissEnded = useSessionRoomStore((state) => state.dismissEnded);
  const dismissedEndedFor = useSessionRoomStore((state) => state.dismissedEndedFor);
  const markAnnounced = useSessionRoomStore((state) => state.markAnnounced);

  /*
    Звонок 1:1 в живых фазах. Читаем именно фазу, а не «есть ли звонок»: `ended` держится ещё
    несколько секунд плашкой «разговор окончен», и прятать под неё полосу комнаты незачем.
  */
  const callActive = useCallStore(
    (state) => state.phase === 'incoming' || state.phase === 'outgoing' || state.phase === 'active'
  );

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const inChatThread = useIsChatThread();
  const routeChatId = useRouterState({
    select: (state) => {
      const match = state.matches?.find((item) =>
        item.routeId.endsWith('/dashboard/chats/$chatId')
      );

      return (match?.params as { chatId?: string } | undefined)?.chatId ?? null;
    },
  });

  /**
   * Прощание: сессия, которую комната только что держала, закончилась.
   *
   * Держим id, фазу и запасной снимок записи. Обычно строка ещё лежит в `mySessions`, и брать
   * надо её: она свежее снимка – например, когда последнее сообщение в чате пришло уже после
   * конца сессии.
   *
   * Снимок нужен для конца досрочного. Хост нажал «Завершить», сервер закрыл строку, и запрос
   * комнаты (`archived: false`) её больше не отдаёт – показывать прощание не из чего, и комната
   * пропадала с экрана молча, хотя переписка жива. Снимок живёт минуту прощания и только ради
   * него.
   */
  const [farewell, setFarewell] = useState<{
    sessionId: string;
    phase: SessionRoomPhase;
    snapshot: SessionRoomEntry | null;
  } | null>(null);
  // Исчезновение участия ещё не означает завершение. Голос держит свои кнопки до проверки.
  const [missingSession, setMissingSession] = useState<SessionRoomEntry | null>(null);
  const shownSessionId = useRef<string | null>(null);
  /** Последняя запись, которую комната показывала: из неё и рисуем прощание, если строка ушла. */
  const shownEntry = useRef<SessionRoomEntry | null>(null);

  useEffect(() => {
    if (session) {
      shownSessionId.current = session.sessionId;
      shownEntry.current = session;
      setFarewell(null);
      setMissingSession(null);

      return;
    }

    const previousId = shownSessionId.current;
    const previousEntry = shownEntry.current;

    if (!previousId) {
      return;
    }

    /*
      Ссылку сбрасываем до разбора: список сессий обновляется чаще, чем меняется фаза, и без
      этого прощание переигрывалось бы заново на каждом рефетче – вместе со своим таймером.
    */
    shownSessionId.current = null;

    /*
      Строки в ответе нет вовсе. Так выглядят сразу три случая: я вышел сам, меня исключили и
      хост завершил сессию досрочно - сервер закрыл её, а запрос комнаты (`archived: false`)
      закрытые не отдаёт. Первым двум прощание не полагается: «Сессия закончилась» тому, кто сам
      нажал «Выйти», это ложь, а исключённому о конце сессии рассказывать нечего.

      Свой уход отсекает отметка, её ставит выход. Остальное проверяем персональной сводкой:
      даже завершённый снимок из памяти списка не сообщает, исключили ли именно этого игрока.
    */
    const leftMyself = useSessionRoomStore.getState().takeExpectedLeave(previousId);
    if (leftMyself || !previousEntry) {
      return;
    }

    setMissingSession(previousEntry);
  }, [session, sessions]);

  useEffect(() => {
    if (!missingSession) {
      return;
    }

    let cancelled = false;
    void queryClient
      .fetchQuery(
        getSessionNotificationSummaryQueryOptions({
          postId: missingSession.postId,
          sessionId: missingSession.sessionId,
          viewerId: profile?.id,
        })
      )
      .then((summary) => {
        if (cancelled) {
          return;
        }
        if (!summary) {
          // Неизвестный статус не повод спрятать микрофон продолжающегося разговора.
          return;
        }
        // Исключение игрока имеет приоритет перед завершением всей сессии.
        if (summary.viewerStatus === 'REJECTED') {
          setMissingSession(null);
          setFarewell(null);
          const voice = useVoiceStore.getState();
          if (
            voice.lastErrorSessionId === missingSession.sessionId &&
            voice.lastError === 'voice.room.banned'
          ) {
            // LiveKit одинаково называет голосовой бан и исключение из состава. Последнее
            // не запрещает новое авторское приглашение; старый локальный отказ ему не мешает.
            voice.setError(null);
          }
          if (voice.lastVoiceSessionId === missingSession.sessionId) voice.forgetVoiceSession();
          void leaveVoice(queryClient, { onlySessionId: missingSession.sessionId });
        } else {
          const phase = resolveRoomPhase({
            status: summary.status,
            startedAt: summary.startedAt,
            expiredAt: summary.expiredAt,
            now: serverNow(),
          });
          if (isClosedPhase(phase)) {
            setMissingSession(null);
            setFarewell({ sessionId: missingSession.sessionId, phase, snapshot: missingSession });
          } else {
            // Участие ещё подтверждено сервером: пустой список может быть устаревшим.
            // Сохраняем микрофон и перечитываем список, не исключая игрока на клиенте.
            void queryClient.invalidateQueries({ queryKey: ['my-sessions'] });
          }
        }

        return undefined;
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [missingSession, profile?.id, queryClient]);

  /*
    Сессия кончилась, а я ещё в её голосе: с 23.09.2026 сервер не рвёт разговор вместе с
    сессией. Пока так, прощание не истекает - вместе с комнатой с экрана ушли бы микрофон и
    выход, а меня по-прежнему слышно. Разговор кончился (вышел сам или сервер закрыл комнату) -
    таймер взводится заново, и плашка живёт свою минуту.
  */
  const voiceSessionId = useVoiceStore((state) =>
    state.phase === 'idle' ? null : (state.room?.sessionId ?? null)
  );
  const previousVoiceSessionId = useRef<string | null>(voiceSessionId);
  useEffect(() => {
    const previousId = previousVoiceSessionId.current;
    previousVoiceSessionId.current = voiceSessionId;
    // Завершённый разговор уже держит farewell и не входит в mySessions. После отключения
    // проверяем, не было ли это исключением из всё ещё открытой голосовой комнаты.
    if (
      !session &&
      previousId &&
      previousId !== voiceSessionId &&
      farewell?.sessionId === previousId &&
      farewell.snapshot
    ) {
      setMissingSession(farewell.snapshot);
    }
  }, [farewell, session, voiceSessionId]);
  const talkingAfterEnd = Boolean(farewell && voiceSessionId === farewell.sessionId);

  useEffect(() => {
    if (!farewell || talkingAfterEnd) {
      return;
    }

    const timer = setTimeout(() => setFarewell(null), ENDED_FAREWELL_MS);

    return () => clearTimeout(timer);
  }, [farewell, talkingAfterEnd]);

  const farewellEntry =
    farewell && dismissedEndedFor !== farewell.sessionId
      ? (sessions.find((entry) => entry.sessionId === farewell.sessionId) ??
        farewell.snapshot ??
        null)
      : null;

  const checkingEntry = missingSession?.sessionId === voiceSessionId ? missingSession : null;
  const entry = session ?? checkingEntry ?? farewellEntry;
  const isChatDeleted = useActiveChatStore((store) => store.isChatDeleted(entry?.chatId));
  /*
    Фазу живой сессии считаем по настоящим часам, а не по метке из хука: `useCurrentSession` сам
    просыпается на старте и на конце сессии и обновляет рендер, поэтому свежие часы здесь и
    означают свежую фазу. Второй «сейчас» дал бы комнате два представления о времени.
  */
  const phaseEntry = session ?? checkingEntry;
  const phase: SessionRoomPhase | null = phaseEntry
    ? resolveRoomPhase({
        status: phaseEntry.status,
        startedAt: phaseEntry.startedAt,
        expiredAt: phaseEntry.expiredAt,
        now: serverNow(),
      })
    : (farewell?.phase ?? null);

  const postId = entry?.postId ?? null;
  const sessionId = entry?.sessionId ?? null;
  const chatId = entry?.chatId ?? null;
  // Тред ровно этой сессии: там ярус голоса стоит над лентой, и комната была бы вторым
  // экземпляром того же самого. Чужой чат не в счёт – там комната как раз нужна.
  const isOwnSessionThread = Boolean(chatId && routeChatId === chatId);

  const { author: lastMessageAuthor, systemActor: lastSystemActor } = useLastMessageAuthor(entry);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  /*
    Раздел переписок – единственное место, где комната спорит не со страницей, а с такой же
    лентой: окно 400×560 встаёт ровно поверх чужих сообщений и отодвигает их резервом снизу.
    Поэтому здесь комната открывается свёрнутой (§5.1) – пилюля перекрывает 56px вместо
    полуэкрана, а окно остаётся одним нажатием.

    Проверка – по маршруту, а не по режиму в сторе: выбранный человеком режим переживает
    страницы, и гасить его ради одного раздела значило бы, что зашедший в переписки теряет окно
    насовсем. Тред собственной сессии сюда не относится – там комнаты нет вовсе.
  */
  const onChatsRoute = pathname.includes('/dashboard/chats');

  /*
    Страница заявки, на которой живёт эта самая сессия (21.09.2026).

    Здесь комната спорит не с чужой лентой, а с карточкой той же сессии: страница уже показывает
    состав, время, голос и переписку, а окно 400x560 рассказывает про них второй раз поверх. Автор
    публиковал сессию и получал окно поверх страницы, которая эту сессию только что объявила.

    Уступаем пилюлей, а не полным скрытием: комната несёт микрофон, и спрятать её целиком значило
    бы отобрать у человека в голосе единственную кнопку выключения (см. `isRoomAllowedHere`,
    там это правило уже отменяли один раз). Пилюля в состоянии «в голосе» несёт полный кластер.

    Сравниваем с `postId` самой сессии, а не просто «мы на какой-то заявке»: чужая заявка о моей
    сессии ничего не рассказывает, и там окно нужно как везде.
  */
  const onOwnPostRoute = Boolean(postId && pathname.includes(`/posts/${postId}`));

  /** Сессия уже на экране целиком: карточкой на своей заявке или лентой своего треда. */
  const sessionIsOnScreen = onChatsRoute || onOwnPostRoute;

  const [expandedByHand, setExpandedByHand] = useState(false);

  /*
    Гостевание в разделе переписок начинается и заканчивается свёрнутой комнатой: пришёл – окно
    уступило ленте, ушёл – вернулся выбранный режим. Развернул руками – окно остаётся до конца
    визита, ходить по тредам, разворачивая его заново, человека никто не заставляет.
  */
  useEffect(() => {
    setExpandedByHand(false);
  }, [sessionIsOnScreen]);

  const expandWindow = useCallback(() => {
    setExpandedByHand(true);
    setMode('window');
    // Ручное «покажи целиком» — единственное разворачивание, которое человек выбрал сам.
    trackRoomExpanded('window');
  }, [setMode]);

  const collapseWindow = useCallback(() => {
    setExpandedByHand(false);
    setMode('mini');
  }, [setMode]);

  /*
    Уход на весь экран и возврат. Живёт эффектом маршрута, а не обработчиком кнопки: на тред
    попадают и по ссылке из письма, и системной «назад», и из списка переписок – а прежний режим
    комнаты обязан вернуться в любом из этих случаев, иначе свёрнутая пилюля превращается в окно
    у того, кто просил её не мешать.
  */
  useEffect(() => {
    if (!isOwnSessionThread) {
      return;
    }

    enterFull();

    return () => exitFull();
  }, [isOwnSessionThread, enterFull, exitFull]);

  /*
    Всё, что человек открыл поверх комнаты, закрываем вместе с ней: открытая шторка и окно
    настроек пережили бы и конец сессии, и переход на страницу, где комнаты нет вовсе, – и висели
    бы поверх пустоты, рассказывая о том, чего уже не видно.
  */
  useEffect(() => {
    if (!entry || (phase && isClosedPhase(phase))) {
      setSheetOpen(false);
      setSettingsOpen(false);
      setInviteOpen(false);
    }
  }, [entry, phase]);

  const openThread = useCallback(() => {
    if (!chatId) {
      return;
    }

    setSheetOpen(false);
    void navigate({ to: '/{-$locale}/dashboard/chats/$chatId', params: { chatId } });
  }, [chatId, navigate]);

  const allowedHere = isRoomAllowedHere({
    isAuth,
    isOwnSessionThread,
    isMobile,
    callActive,
    pathname,
  });

  const visibleSessionId =
    isBrowser && entry && phase && sessionId && postId && !isChatDeleted && allowedHere
      ? sessionId
      : null;

  useEffect(() => {
    setVisibleSessionId(visibleSessionId);

    return () => setVisibleSessionId(null);
  }, [setVisibleSessionId, visibleSessionId]);

  /*
    Первое появление сессии (бриф комнаты §4, бриф приглашений §6.1). До 14.09.2026 правила в
    коде не было: комната рисовалась в режиме из стора, и у человека, свернувшего её в пилюлю час
    назад, новая сессия после «Вступить» появлялась пилюлей в углу – «попапа при вступлении» не
    было вовсе.

    Десктоп – окно; за собственным жестом вступления ещё и с фокусом. В разделе переписок остаётся
    пилюля: окно накрыло бы чужую ленту. Телефон – только свёрнутая полоса, раскрывает её сам
    человек. Объявленная сессия запоминается – F5 не переигрывает.
  */
  const liveSessionId = session?.sessionId ?? null;

  useEffect(() => {
    if (!isBrowser || !liveSessionId || !allowedHere) {
      return;
    }

    const store = useSessionRoomStore.getState();
    const announced = store.announcedSessionIds;

    if (announced?.includes(liveSessionId)) {
      return;
    }

    const byGesture = store.takeExpectedJoin(liveSessionId);
    markAnnounced(liveSessionId);

    // Память ещё не заведена (первый запуск после выката): появившееся само отмечаем молча, чтобы
    // не распахнуть окно всем, кто держал комнату свёрнутой.
    if (!byGesture && announced === null) {
      return;
    }

    /*
      Телефон – только свёрнутая полоса, без шторки даже за собственным жестом (02.10.2026,
      решение владельца): раскрытый виджет при входе закрывал пол-экрана поверх того, ради чего
      человек пришёл, а развернуть полосу – одно касание.
    */
    if (isMobile) {
      return;
    }

    // Сессия уже на экране (свой тред, своя заявка): объявлять её окном нечем, о ней и так
    // рассказывает страница. Само по себе появление комнаты при этом остаётся - пилюлей.
    if (sessionIsOnScreen) {
      return;
    }

    setMode('window');
    setExpandedByHand(byGesture);
  }, [isBrowser, liveSessionId, allowedHere, isMobile, sessionIsOnScreen, markAnnounced, setMode]);

  const dismiss = useCallback(() => {
    if (sessionId) {
      dismissEnded(sessionId);
    }

    setFarewell(null);
  }, [dismissEnded, sessionId]);

  // Комната целиком клиентская: и правило показа, и режим считаются по часам браузера, ширине
  // экрана и localStorage – на сервере ни одного из трёх нет.
  if (!isBrowser || !entry || !phase || !sessionId || !postId) {
    return null;
  }

  /*
    Автор удалил переписку сессии – комната уходит с экрана целиком (12.09.2026).

    Раньше она оставалась висеть и писала «Чат удалён» в строке превью: голос-то жив. Но на
    экране у участника это выглядело как застрявший обломок – окно разговора, в котором
    разговаривать нельзя, и убрать его нечем: кнопка закрытия у комнаты появляется только
    после конца сессии.

    Голос от этого не обрывается: он живёт в провайдере выше и продолжает играть. Вернуться в
    него можно со страницы заявки, где стоит тот же блок.
  */
  if (isChatDeleted) {
    return null;
  }

  if (!allowedHere) {
    return null;
  }

  const surface: SessionRoomSurfaceProps = {
    session: entry,
    phase,
    // Пока чата нет, кнопки «на весь экран» нет и в помине: открывать нечего, и поверхность
    // сама скажет об этом словами.
    onFullscreen: chatId ? openThread : null,
    onClose: dismiss,
    lastMessageAuthor,
    lastSystemActor,
  };

  /*
    «Настройки сессии» – существующее окно `SessionDetailsDialog` через обёртку по id: комната
    живёт выше роутера, и данных заявки у неё нет вовсе. Диалог висит рядом с поверхностью, а не
    внутри неё: пункт меню закрывает и меню, и шторку, и диалог, живший внутри, уехал бы вместе
    с ними.
  */
  const settingsDialog = (
    <SessionDetailsDialogById
      postId={postId}
      sessionId={sessionId}
      open={settingsOpen}
      onOpenChange={setSettingsOpen}
    />
  );

  // «Позвать в сессию» – по той же причине рядом с поверхностью, а не внутри неё.
  const inviteDialog =
    postId && sessionId ? (
      <InviteToSessionDialogById
        postId={postId}
        sessionId={sessionId}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
      />
    ) : null;

  if (isMobile) {
    return (
      <>
        <SessionRoomBar
          {...surface}
          inChatThread={inChatThread}
          onOpenSessionSettings={() => setSettingsOpen(true)}
          onExpand={() => {
            setSheetOpen(true);
            trackRoomExpanded('sheet');
          }}
        />
        <SessionRoomSheet
          {...surface}
          inChatThread={inChatThread}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onOpenSessionSettings={() => setSettingsOpen(true)}
          onOpenInvite={() => setInviteOpen(true)}
        />
        {settingsDialog}
        {inviteDialog}
      </>
    );
  }

  return (
    <>
      {mode === 'mini' || (sessionIsOnScreen && !expandedByHand) ? (
        <SessionRoomPill
          {...surface}
          onExpand={expandWindow}
          onOpenSessionSettings={() => setSettingsOpen(true)}
        />
      ) : (
        <SessionRoomWindow
          {...surface}
          onCollapse={collapseWindow}
          onOpenSessionSettings={() => setSettingsOpen(true)}
          onOpenInvite={() => setInviteOpen(true)}
          /* Развернули руками – фокус переезжает в окно: иначе он остаётся на `body`, и Escape,
             которым окно сворачивают, ни до кого не доходит. */
          autoFocus={expandedByHand}
        />
      )}
      {settingsDialog}
      {inviteDialog}
    </>
  );
};
